/**
 * **主题文档的解析与校验** —— 纯函数，零 DOM。
 *
 * ## 解析做什么
 *
 * ```text
 *   ThemeDocument（可能带 extends）
 *        │  ① 展开继承链（祖先在前，环要报错）
 *        │  ② 合并：palette / tokens / recipes / css / layout
 *        │  ③ 调色板 → 派生令牌
 *        │  ④ 主题显式写的 tokens 覆盖派生结果
 *        ▼
 *   ResolvedTheme（可以直接交给 applyTheme）
 * ```
 *
 * ## ⚠️ 为什么环检测要「报错」而不是「静默截断」
 *
 * `extends` 形成环时（A→B→A），最省事的写法是「走过的就不再走」。
 * 那样会**静默丢掉一层主题**，而症状是「某些颜色没生效」——
 * 使用者只会以为是自己写错了令牌名。这类「悄悄少做一件事」的失败
 * 在这个仓库里被记过很多次，所以这里直接返回错误。
 */
import type {
  RecipeSlot,
  RegionPlacement,
  ResolvedTheme,
  ThemeDocument,
  ThemeLayout,
  ThemePalette,
} from '../types/theme'
import { THEME_FORMAT_VERSION } from '../types/theme'
import { deriveTokens, tokenKey } from './derive'

/* ══════════════════════════════════════════════════════════════════
   合并原语
   ══════════════════════════════════════════════════════════════════ */

/** 浅合并：`undefined` 不算「有值」，否则子主题写一半会把祖先抹掉 */
function mergeFlat<T extends object>(base: T | undefined, over: T | undefined): T | undefined {
  if (!base) return over
  if (!over) return base
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) }
  for (const [k, v] of Object.entries(over)) {
    if (v !== undefined) out[k] = v
  }
  return out as T
}

function mergePalette(
  base: ThemePalette | undefined,
  over: ThemePalette | undefined,
): ThemePalette | undefined {
  if (!base) return over
  if (!over) return base
  return {
    ...base,
    ...over,
    categories: mergeFlat(base.categories, over.categories),
    sockets: mergeFlat(base.sockets, over.sockets),
    severity: mergeFlat(base.severity, over.severity),
  }
}

function mergePlacement(
  base: RegionPlacement | undefined,
  over: RegionPlacement | undefined,
): RegionPlacement {
  return { ...(base ?? {}), ...(over ?? {}) }
}

function mergeLayout(base: ThemeLayout | undefined, over: ThemeLayout | undefined): ThemeLayout {
  if (!base) return over ?? {}
  if (!over) return base

  const regions: ThemeLayout['regions'] = {}
  for (const editor of new Set([
    ...Object.keys(base.regions ?? {}),
    ...Object.keys(over.regions ?? {}),
  ])) {
    const a = base.regions?.[editor] ?? {}
    const b = over.regions?.[editor] ?? {}
    const merged: Record<string, RegionPlacement> = {}
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
      merged[key] = mergePlacement(a[key], b[key])
    }
    regions[editor] = merged
  }

  return {
    ...base,
    ...over,
    chrome: mergeFlat(base.chrome, over.chrome),
    split: mergeFlat(base.split, over.split),
    canvas: mergeFlat(base.canvas, over.canvas),
    regions: Object.keys(regions).length ? regions : base.regions,
  }
}

/* ══════════════════════════════════════════════════════════════════
   规范化
   ══════════════════════════════════════════════════════════════════ */

/**
 * 把一份「可能是人手写的」文档收拾干净。
 *
 * 手写 JSON 主题最容易出的三类问题，这里一次性兜住：
 *   · 令牌键漏了 `--np-` 前缀（静默失效）；
 *   · `recipes` 里写了空字符串（等于「选了个不存在的配方」）；
 *   · `id` 带空格 / 大写（会进选择器和 localStorage 键）。
 */
export function normalizeTheme(doc: ThemeDocument): ThemeDocument {
  const tokens: Record<string, string> = {}
  for (const [k, v] of Object.entries(doc.tokens ?? {})) {
    if (typeof v !== 'string' || !v.trim()) continue
    tokens[tokenKey(k)] = v.trim()
  }

  const recipes: Partial<Record<RecipeSlot, string>> = {}
  for (const [k, v] of Object.entries(doc.recipes ?? {})) {
    if (typeof v === 'string' && v.trim()) recipes[k as RecipeSlot] = v.trim()
  }

  return {
    ...doc,
    version: doc.version ?? THEME_FORMAT_VERSION,
    id: slugifyThemeId(doc.id),
    tokens: Object.keys(tokens).length ? tokens : undefined,
    recipes: Object.keys(recipes).length ? recipes : undefined,
  }
}

/** 主题 id 变成「能进选择器、能当 localStorage 键」的样子 */
export function slugifyThemeId(raw: string): string {
  return (
    raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'theme'
  )
}

/* ══════════════════════════════════════════════════════════════════
   解析
   ══════════════════════════════════════════════════════════════════ */

export interface ResolveResult {
  theme?: ResolvedTheme
  /** 解析失败的原因（继承缺失 / 成环 / 版本不认识）。空数组 = 成功 */
  problems: string[]
}

/**
 * 展开继承链并解析。
 *
 * @param doc    要解析的主题（应当已经 `normalizeTheme` 过）
 * @param lookup 按 id 找**祖先**主题的函数；找不到就报问题，不猜
 */
export function resolveTheme(
  doc: ThemeDocument,
  lookup: (id: string) => ThemeDocument | undefined,
): ResolveResult {
  const problems: string[] = []
  const chain: ThemeDocument[] = []
  const seen = new Set<string>()

  let cur: ThemeDocument | undefined = doc
  while (cur) {
    if (seen.has(cur.id)) {
      /*
       * ⚠️ 成环。**不截断** —— 明确报出来。
       * 截断的话症状是「某几个颜色没生效」，没人查得出来。
       */
      problems.push(`\`extends\` 成环：${[...seen, cur.id].join(' → ')}`)
      return { problems }
    }
    if ((cur.version ?? THEME_FORMAT_VERSION) !== THEME_FORMAT_VERSION) {
      problems.push(
        `主题 \`${cur.id}\` 的格式版本是 ${cur.version}，本版本只认 ${THEME_FORMAT_VERSION}`,
      )
      return { problems }
    }
    seen.add(cur.id)
    chain.unshift(cur) // 祖先在前
    if (!cur.extends) break
    const parent = lookup(cur.extends)
    if (!parent) {
      problems.push(`主题 \`${cur.id}\` 继承了不存在的主题 \`${cur.extends}\``)
      return { problems }
    }
    cur = parent
  }

  /* ── 逐层合并（祖先在前） ── */
  let palette: ThemePalette | undefined
  let tokens: Record<string, string> = {}
  let recipes: Partial<Record<RecipeSlot, string>> = {}
  const cssParts: string[] = []
  let layout: ThemeLayout = {}
  let scheme: 'dark' | 'light' | undefined

  for (const layer of chain) {
    palette = mergePalette(palette, layer.palette)
    tokens = { ...tokens, ...(layer.tokens ?? {}) }
    recipes = { ...recipes, ...(layer.recipes ?? {}) }
    if (layer.css?.trim()) cssParts.push(layer.css.trim())
    layout = mergeLayout(layout, layer.layout)
    if (layer.scheme) scheme = layer.scheme
  }

  const finalScheme = scheme ?? 'dark'

  /*
   * ⚠️ 顺序不能反：**派生在前、显式 tokens 在后**。
   * 反过来会让「我只改一个 alpha」变得不可能。
   */
  const derived = deriveTokens(palette, finalScheme, layout.density)
  const merged = { ...derived, ...tokens }

  /*
   * 布局里那些「本质上是尺寸」的字段折成令牌。
   *
   * ⚠️ 这样做的意义是**只留一条通道**：主题写 `layout.split.size`
   * 和直接写 `tokens['--np-split-size']` 得到同一个结果，
   * 而 `applyTheme` 不用认识 `layout` 的任何一个字段 ——
   * 它只管往 DOM 上写令牌。少一条通道就少一处会漂的地方。
   */
  if (layout.split?.size != null) merged['--np-split-size'] = `${layout.split.size}px`

  /*
   * 缺强调色时补一个中性兜底。
   *
   * ⚠️ 没有它的话，`var(--np-accent)` 会是个**无效值**，
   * 于是所有用到它的声明（边框、焦点环、开关）整条作废 ——
   * 症状是「选中环不见了」，而看起来像样式没写。
   */
  if (!merged['--np-accent']) {
    Object.assign(merged, deriveTokens({ accent: finalScheme === 'dark' ? '#f0a04b' : '#b56a10' }))
  }

  return {
    problems,
    theme: {
      doc: normalizeTheme({ ...chain[chain.length - 1]!, tokens: merged }),
      id: doc.id,
      name: doc.name,
      hint: doc.hint,
      scheme: finalScheme,
      tokens: merged,
      recipes,
      css: cssParts.join('\n\n'),
      layout,
      chain: chain.map((c) => c.id),
    },
  }
}

/* ══════════════════════════════════════════════════════════════════
   校验
   ══════════════════════════════════════════════════════════════════ */

/**
 * 结构性问题（不是审美问题）。
 *
 * ⚠️ 这里**不检查令牌名是否存在** —— 那需要拿到 `theme.css` 的令牌全集，
 * 属于跨文件的账，交给 `verify:theme`（它有文件系统的访问权）。
 * 这个函数只做「不依赖外部信息的自洽检查」，所以能在浏览器里跑。
 */
export function validateTheme(doc: ThemeDocument, knownIds: readonly string[] = []): string[] {
  const problems: string[] = []
  const id = slugifyThemeId(doc.id)

  if (!doc.name?.trim()) problems.push('缺少 `name`（菜单里会显示成空白）')
  if (doc.id !== id) problems.push(`\`id\` 会被规范成 \`${id}\`（只能用小写字母、数字、\`.\`、\`_\`、\`-\`）`)
  if (knownIds.includes(id)) problems.push(`\`id\` 与已有主题重复：\`${id}\``)

  if (doc.extends && !knownIds.includes(doc.extends)) {
    problems.push(`继承了不存在的主题 \`${doc.extends}\``)
  }
  if (doc.extends === id) problems.push('`extends` 指向了自己')

  const mo = doc.layout?.motion
  if (mo !== undefined && !['off', 'subtle', 'normal'].includes(mo)) {
    problems.push(`\`layout.motion\` 只能是 off / subtle / normal，收到 \`${mo}\``)
  }
  const d = doc.layout?.density
  if (d && !['compact', 'normal', 'comfortable'].includes(d)) {
    problems.push(`\`layout.density\` 只能是 compact / normal / comfortable，收到 \`${d}\``)
  }

  for (const [editor, regions] of Object.entries(doc.layout?.regions ?? {})) {
    for (const [key, p] of Object.entries(regions)) {
      /*
       * ⚠️ `edge` 对**两种模式都合法**：`dock` 时它是"贴哪条边"，
       * `float` 时它是"浮在哪条边旁边"。第一版把 float + edge 判成错，
       * 于是「浮起来的工具栏贴左边」这个最自然的写法被自己的校验挡住了。
       * `corner` 才只对 float 有意义。
       */
      if (p.mode === 'dock' && p.corner) {
        problems.push(`${editor}.${key}：dock 模式用不到 corner（那是 float 的锚角）`)
      }
    }
  }

  return problems
}
